import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'; // , useEffect, useMemo, useState
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { CancelToken, isCancel } from 'axios';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import debounce from 'debounce-promise';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Tooltip from 'react-tooltip';
import css from 'styled-jsx/css';

import TemplateService from '../../Services/TemplateService';
import { IMAGE } from '../../Constants/WidgetTypes';
import { BASIC_SELECT_STYLES } from '../../Styles/SelectStyles';
import { COMPONENT_OVERRIDE, TOOLTIP_OVERRIDE } from '../../Styles/Z-INDEX';

export default memo(ConfigureImageWidget);

const baseStyle = {
  backgroundColor: '#fafafa',
  borderWidth: 3,
  borderRadius: 5,
  borderColor: 'var(--brand-primary)',
  borderStyle: 'dashed',
  color: 'var(--muted-gray)',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const acceptStyle = { borderColor: 'var(--alert-confirmation)' };
const focusedStyle = { borderColor: 'var(--brand-primary-alert)' };
const rejectStyle = { borderColor: 'var(--warning-yellow)' };

function ConfigureImageWidget(props) {
  const {
    activeImageCoordinate,
    clearImage,
    datapointsMap,
    gtemplateID,
    handleAddImageOverlaySignal,
    handleCoordinateClick,
    handleImageClick,
    handleImageSelect,
    handleRemoveImageOverlaySignal,
    image,
    imageOverlayRef,
    isEdit,
    orgID,
    toggleImageOverlayModal,
    widgetType,
  } = props;
  const accept = { 'image/*': [] };
  const onDrop = useCallback(
    (acceptedFiles) => {
      handleImageSelect(acceptedFiles);
    },
    [handleImageSelect]
  );
  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
    isFocused,
  } = useDropzone({ accept, maxSize: 5000000, multiple: false, onDrop });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  return widgetType === IMAGE ? (
    <div className="flex-fill row no-gutters align-items-stretch justify-content-center position-relative">
      {!isEdit ? (
        <button
          className={image ? 'btn btn-link p-0 position-absolute' : 'd-none'}
          disabled={!image}
          onClick={clearImage}
          style={{ right: 0, top: 0, zIndex: COMPONENT_OVERRIDE }}
        >
          <FontAwesomeIcon className="text-danger" icon={faTimes} />
        </button>
      ) : null}
      {image ? (
        <div className="position-absolute h-100 w-100 d-flex align-items-center justify-content-center">
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <img
            alt="overlay"
            className="img-fluid mx-auto mh-100 rounded rounded-lg"
            id="image-overlay"
            onClick={handleImageClick}
            onKeyDown={handleImageClick}
            ref={imageOverlayRef}
            src={image}
            style={{ cursor: 'grab' }}
          />
        </div>
      ) : (
        <div
          {...getRootProps({ style })}
          className="col-12 d-flex flex-column align-items-center justify-content-center"
        >
          <input {...getInputProps()} />
          <p className="mb-0">
            {isDragActive
              ? 'Drop the file here ...'
              : `Drag 'n' drop a photo here, or click to select one`}
          </p>
        </div>
      )}
      {props.imageCoordinatesMap?.size
        ? [...props.imageCoordinatesMap.entries()].map(
            ([coordinates, config]) => {
              const [x, y] = coordinates.split('-');
              const {
                datapoint: { label: collectionName } = {},
                signal: { label: signalName } = {},
              } = config || {};

              const {
                className: overlayMarkerClassName,
                styles: overlayMarkerStyle,
              } = getOverlayMarkerStyle(x, y);

              return (
                <button
                  className={`btn p-0 position-fixed shadow-lg overlay-marker ${overlayMarkerClassName}`}
                  data-for={coordinates}
                  data-tip
                  key={coordinates}
                  onClick={() => handleCoordinateClick(x, y, config)}
                >
                  <Tooltip
                    backgroundColor="var(--brand-primary)"
                    id={coordinates}
                  >
                    <p className="mb-0 text-white text-center">
                      {collectionName}
                    </p>
                    <p className="mb-0 text-white text-center">{signalName}</p>
                  </Tooltip>
                  {overlayMarkerStyle}
                </button>
              );
            }
          )
        : null}
      {props.activeImageCoordinate?.size ? (
        <ImageOverlayModal
          activeImageCoordinate={activeImageCoordinate}
          datapointsMap={datapointsMap}
          gtemplateID={gtemplateID}
          handleAddImageOverlaySignal={handleAddImageOverlaySignal}
          handleRemoveImageOverlaySignal={handleRemoveImageOverlaySignal}
          orgID={orgID}
          toggleImageOverlayModal={toggleImageOverlayModal}
        />
      ) : null}
      <style jsx global>{`
        @keyframes pulse {
          0% {
            background-color: var(--brand-primary);
          }
          100% {
            background-color: var(--brand-primary-alert);
          }
        }
      `}</style>
    </div>
  ) : null;
}

ConfigureImageWidget.propTypes = {
  activeImageCoordinate: PropTypes.object,
  clearImage: PropTypes.func,
  datapointsMap: PropTypes.arrayOf(PropTypes.object),
  gtemplateID: PropTypes.string,
  handleAddImageOverlaySignal: PropTypes.func,
  handleCoordinateClick: PropTypes.func,
  handleImageClick: PropTypes.func,
  handleImageSelect: PropTypes.func,
  handleRemoveImageOverlaySignal: PropTypes.func,
  image: PropTypes.string,
  imageCoordinatesMap: PropTypes.object,
  imageOverlayRef: PropTypes.object,
  isEdit: PropTypes.bool,
  orgID: PropTypes.string,
  toggleImageOverlayModal: PropTypes.func,
  widgetType: PropTypes.string,
};

function ImageOverlayModal(props) {
  const [x, y, imageOverlay] = [...props.activeImageCoordinate.values()];

  const [datapoint, setCollection] = useState(imageOverlay?.datapoint);
  const [signal, setSignal] = useState(imageOverlay?.signal);
  const [error, setError] = useState('');
  const [requestPending, setRequestPending] = useState(false);

  const service = useMemo(() => new TemplateService(), []);
  let source = CancelToken.source();

  const fetchOutputSignals = async (searchTerm) => {
    try {
      setRequestPending(true);
      const params = {
        limit: 100,
        orgID: props.orgID,
        search: 'name',
        searchTerm,
      };

      source.cancel();
      source = CancelToken.source();
      const { data } = await service.searchOutputSignals(
        props.gtemplateID,
        params,
        source.token
      );
      setRequestPending(false);
      return data.map((os) => ({ label: os.name, value: os }));
    } catch (err) {
      if (isCancel(err)) return;
      const reqError = err.response?.data?.message ?? err.message;
      setError(reqError);
      setRequestPending(false);
      return [];
    }
  };
  useEffect(() => source?.cancel, []);

  return (
    <Modal
      backdrop="static"
      centered
      isOpen={!!props.activeImageCoordinate.size}
      toggle={props.toggleImageOverlayModal}
    >
      <ModalHeader
        className="border-bottom-0 modal-title-text"
        toggle={props.toggleImageOverlayModal}
      >
        Choose signal to display on image
      </ModalHeader>
      <ModalBody className="container-fluid">
        <div className="row">
          <div className="col-12 form-group">
            <label htmlFor="signalCollection" className="modal-inputlabel-text">
              Signal Collection
            </label>
            <Select
              isSearchable
              maxMenuHeight={150}
              name="signalCollection"
              onChange={setCollection}
              options={props.datapointsMap}
              placeholder="Select Collection"
              styles={BASIC_SELECT_STYLES}
              value={datapoint}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 form-group">
            <label htmlFor="signal" className="modal-inputlabel-text">
              Signal
            </label>
            {datapoint?.value === 'outputSignal' ? (
              <AsyncSelect
                cacheOptions
                defaultOptions
                disabled={!datapoint.value}
                isLoading={requestPending}
                loadOptions={debounce(fetchOutputSignals, 1000)}
                maxMenuHeight={150}
                name="signal"
                onChange={setSignal}
                placeholder="Select Signal"
                styles={BASIC_SELECT_STYLES}
                value={signal}
              />
            ) : (
              <Select
                disabled={!datapoint?.value}
                isSearchable
                maxMenuHeight={150}
                name="signal"
                onChange={setSignal}
                options={datapoint?.values}
                placeholder="Select Signal"
                styles={BASIC_SELECT_STYLES}
                value={signal}
              />
            )}
          </div>
        </div>
        <div className="row">
          <p className="col-12 mb-0 three-quarter-text text-danger">{error}</p>
        </div>
      </ModalBody>
      <ModalFooter className="border-top-0">
        <button
          className="btn btn-danger py-0 px-3"
          onClick={() => props.handleRemoveImageOverlaySignal(`${x}-${y}`)}
        >
          Remove
        </button>
        <button
          className="btn modal-button-secondary py-0 px-3"
          disabled={!datapoint?.value || !signal?.value}
          onClick={() =>
            props.handleAddImageOverlaySignal(`${x}-${y}`, {
              ...imageOverlay,
              datapoint,
              signal,
            })
          }
        >
          Update
        </button>
      </ModalFooter>
    </Modal>
  );
}

ImageOverlayModal.propTypes = {
  activeImageCoordinate: PropTypes.object,
  datapointsMap: PropTypes.arrayOf(PropTypes.object),
  gtemplateID: PropTypes.string,
  handleAddImageOverlaySignal: PropTypes.func,
  handleRemoveImageOverlaySignal: PropTypes.func,
  orgID: PropTypes.string,
  toggleImageOverlayModal: PropTypes.func,
};

function getOverlayMarkerStyle(x, y) {
  return css.resolve`
    .overlay-marker {
      animation-direction: alternate;
      animation-duration: 2s;
      animation-iteration-count: infinite;
      animation-name: pulse;
      border-radius: 50%;
      height: 10px;
      left: ${x}px;
      -moz-animation-direction: alternate;
      -moz-animation-duration: 2s;
      -moz-animation-iteration-count: infinite;
      -moz-animation-name: pulse;
      top: ${y}px;
      -webkit-animation-direction: alternate;
      -webkit-animation-duration: 2s;
      -webkit-animation-iteration-count: infinite;
      -webkit-animation-name: pulse;
      width: 10px;
      z-index: ${TOOLTIP_OVERRIDE};
    }
  `;
}
